import React, { useState } from "react";
import { Check, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";

const FEATURES = [
  "PDF blueprint upload & render",
  "Scale calibration",
  "Trace & count takeoff tools",
  "Estimating matrix",
  "Progress billing ledger",
  "UK CIS & VAT reverse charge",
  "Client card & BACS payments",
  "Branded PDF & CSV export",
];

const AI_FEATURES = [
  "AI quantity extraction from drawings",
  "AI drawing-scale detection",
  "AI rate suggestions",
];

const PLAN_META = {
  free: { blurb: "Try the full workflow on one live job.", limit: "1 active project" },
  growth: { blurb: "Run several jobs side by side.", limit: "Up to 5 active projects" },
  scale: { blurb: "No limits for busy firms.", limit: "Unlimited active projects" },
};

const gbp = (n) => (n % 1 === 0 ? `£${n}` : `£${Number(n).toFixed(2)}`);

const PricingCards = ({ plans = [], onSelect, currentPlan }) => {
  const [interval, setInterval] = useState("monthly");
  const annual = interval === "annual";
  return (
    <div>
      {/* Monthly / Annual toggle */}
      <div className="flex justify-center mb-10">
        <div className="inline-flex items-center rounded-full bg-slate-100 p-1" data-testid="billing-interval-toggle">
          <button
            type="button"
            data-testid="billing-toggle-monthly"
            onClick={() => setInterval("monthly")}
            className={`px-5 py-2 rounded-full text-sm font-semibold transition-colors ${!annual ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}
          >
            Monthly
          </button>
          <button
            type="button"
            data-testid="billing-toggle-annual"
            onClick={() => setInterval("annual")}
            className={`px-5 py-2 rounded-full text-sm font-semibold transition-colors inline-flex items-center gap-2 ${annual ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}
          >
            Annual
            <span className="text-[10px] font-bold uppercase tracking-wide text-emerald-700 bg-emerald-100 rounded-full px-1.5 py-0.5">2 months free</span>
          </button>
        </div>
      </div>

      <div className="grid md:grid-cols-3 gap-6">
      {plans.map((p) => {
        const highlighted = p.key === "growth";
        const meta = PLAN_META[p.key] || {};
        const isCurrent = currentPlan === p.key;
        const isPaid = p.key !== "free";
        const monthly = p.price_monthly ?? p.price ?? 0;
        const annualTotal = p.price_annual ?? monthly * 10;
        const annualEquiv = p.price_annual_monthly_equiv ?? (annualTotal / 12);
        const bigPrice = isPaid && annual ? annualEquiv : monthly;
        return (
          <div
            key={p.key}
            data-testid={`pricing-card-${p.key}`}
            className={`relative bg-white rounded-xl p-7 flex flex-col ${
              highlighted ? "border-2 border-blue-600 shadow-lg" : "border border-slate-200 shadow-sm"
            }`}
          >
            {highlighted && (
              <span className="absolute -top-3 left-7 bg-blue-600 text-white text-[11px] font-semibold uppercase tracking-wider px-3 py-1 rounded-full">
                Most popular
              </span>
            )}
            <h3 className="font-display text-lg font-bold text-navy">{p.name}</h3>
            <p className="text-sm text-slate-500 mt-1 min-h-[40px]">{meta.blurb}</p>
            <div className="mt-4 flex items-end gap-1">
              <span className="font-display text-4xl font-bold text-slate-900 tabular" data-testid={`plan-price-${p.key}`}>{gbp(bigPrice)}</span>
              <span className="text-slate-500 text-sm mb-1">/mo</span>
            </div>
            <div className="min-h-[20px] mt-1">
              {isPaid && annual && (
                <p className="text-xs text-emerald-700 font-medium" data-testid={`plan-annual-note-${p.key}`}>
                  Billed {gbp(annualTotal)}/yr — save {gbp(monthly * 2)}
                </p>
              )}
            </div>
            <div className="mt-2 inline-flex items-center gap-2 text-sm font-semibold text-blue-700 bg-blue-50 rounded-md px-3 py-1.5 self-start">
              {meta.limit}
            </div>
            <p className="mt-2 text-sm text-slate-600" data-testid={`plan-seats-${p.key}`}>
              <span className="font-medium text-slate-800">{p.seats} user seat{p.seats > 1 ? "s" : ""}</span>
              {isPaid && <span className="text-slate-400"> · extra seats {annual ? "£70/yr" : "£7/mo"} each</span>}
            </p>
            <p className="mt-2 text-xs text-slate-400">
              {p.key === "free" ? "Free forever · no card required" : annual ? "Billed annually · 2 months free · cancel anytime" : "Billed monthly · renews every 30 days · cancel anytime"}
            </p>
            <Button
              data-testid={`select-plan-${p.key}`}
              onClick={() => onSelect && onSelect(p.key, isPaid ? interval : "monthly")}
              disabled={isCurrent}
              className={`mt-6 w-full ${highlighted ? "bg-blue-600 hover:bg-blue-700" : "bg-slate-900 hover:bg-slate-800"}`}
            >
              {isCurrent ? "Current plan" : p.key === "free" ? "Start free" : `Choose ${p.name}`}
            </Button>
            <ul className="mt-6 space-y-2.5">
              {FEATURES.map((f) => (
                <li key={f} className="flex items-start gap-2 text-sm text-slate-600">
                  <Check className="h-4 w-4 text-blue-600 mt-0.5 shrink-0" />
                  {f}
                </li>
              ))}
            </ul>
            <div className="mt-4 pt-4 border-t border-slate-100">
              <div className="flex items-center gap-1.5 mb-2.5">
                <Sparkles className="h-4 w-4 text-blue-600" />
                <span className="text-xs font-semibold uppercase tracking-wider text-blue-700">Fair-use AI included</span>
              </div>
              <ul className="space-y-2.5">
                {AI_FEATURES.map((f) => (
                  <li key={f} className="flex items-start gap-2 text-sm text-slate-600">
                    <Sparkles className="h-4 w-4 text-blue-500 mt-0.5 shrink-0" />
                    {f}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        );
      })}
      </div>
    </div>
  );
};

export default PricingCards;
